import { useState } from "react";
import { toast } from "sonner";
import { BeatShell } from "@/components/nutrition/NutritionParts";
import { Briefing } from "./Shared";
import { Map, Check, X } from "lucide-react";

// Top-down clinic floor-plan. Pick a role chip, then tap the room it belongs in.
// Roles are unique (one room each); graded correct-room / total.
export function FloorPlanBeat({ beat, eyebrow, showBriefing, c, accent, onDone }) {
  const [assign, setAssign] = useState({}); // zoneId -> roleId
  const [activeRole, setActiveRole] = useState(null);
  const [res, setRes] = useState(null);
  const checked = !!res;

  const roleById = (id) => beat.roles.find((r) => r.id === id);
  const usedRoles = new Set(Object.values(assign));

  const tapZone = (zoneId) => {
    if (checked) return;
    if (assign[zoneId]) { // tap an assigned room to clear it
      setAssign((a) => { const n = { ...a }; delete n[zoneId]; return n; });
      return;
    }
    if (!activeRole) return toast.error("Pick a zone label first, then tap a room.");
    setAssign((a) => {
      const n = { ...a };
      for (const z of Object.keys(n)) if (n[z] === activeRole) delete n[z];
      n[zoneId] = activeRole;
      return n;
    });
    setActiveRole(null);
  };

  const onPrimary = () => {
    if (!checked) {
      if (Object.keys(assign).length < beat.zones.length) return toast.error("Label every room.");
      const correctCount = beat.zones.filter((z) => assign[z.id] === z.correct).length;
      const full = beat.full ?? 20;
      setRes({ earned: Math.round((full * correctCount) / beat.zones.length), max: full, correctCount,
        detail: `${correctCount}/${beat.zones.length} zones placed correctly`, key: beat.key, label: beat.label });
    } else onDone(res);
  };

  return (
    <BeatShell Icon={beat.icon || Map} eyebrow={eyebrow} title={beat.title} subtitle={beat.prompt}
      primaryLabel={checked ? (beat.last ? "See debrief →" : "Continue →") : "Check layout"} onPrimary={onPrimary} primaryTestId={`${beat.key}-primary-btn`} accent={accent}>
      {showBriefing && <Briefing c={c} />}

      {/* role palette */}
      {!checked && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Tap a zone label, then tap the room it belongs in</div>
          <div className="flex flex-wrap gap-2" data-testid={`${beat.key}-roles`}>
            {beat.roles.map((r) => {
              const used = usedRoles.has(r.id);
              const sel = activeRole === r.id;
              return (
                <button key={r.id} type="button" disabled={used} onClick={() => setActiveRole(sel ? null : r.id)} data-testid={`${beat.key}-role-${r.id}`}
                  className={`flex items-center gap-1.5 rounded-full border-2 px-3 py-1.5 text-xs font-semibold transition-colors ${used ? "opacity-30 border-slate-200 bg-slate-100 text-slate-400" : sel ? "text-white" : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"}`}
                  style={sel ? { background: r.color, borderColor: r.color } : undefined}>
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: r.color }} /> {r.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* floor plan */}
      <div className="relative w-full rounded-lg border-2 border-slate-400 bg-slate-100 overflow-hidden" style={{ paddingTop: "62%" }} data-testid={`${beat.key}-plan`}>
        <div className="absolute inset-0">
          {beat.zones.map((z) => {
            const roleId = assign[z.id];
            const role = roleId ? roleById(roleId) : null;
            const ok = checked && roleId === z.correct;
            const bad = checked && roleId && roleId !== z.correct;
            return (
              <button key={z.id} type="button" disabled={checked} onClick={() => tapZone(z.id)} data-testid={`${beat.key}-zone-${z.id}`}
                className={`absolute rounded-md border-2 flex flex-col items-center justify-center text-center p-1.5 transition-colors ${ok ? "border-emerald-500 bg-emerald-50" : bad ? "border-rose-400 bg-rose-50" : role ? "bg-white" : "border-dashed border-slate-300 bg-white/70 hover:bg-white"}`}
                style={{ left: `${z.x}%`, top: `${z.y}%`, width: `${z.w}%`, height: `${z.h}%`, borderColor: role && !checked ? role.color : undefined }}>
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide leading-tight">{z.label}</span>
                {role && <span className="text-[10px] font-semibold leading-tight mt-0.5" style={{ color: checked ? (ok ? "#047857" : "#be123c") : role.color }}>{role.label}</span>}
                {checked && (ok ? <Check className="w-3.5 h-3.5 text-emerald-600 mt-0.5" /> : bad ? <X className="w-3.5 h-3.5 text-rose-500 mt-0.5" /> : null)}
                {checked && bad && <span className="text-[9px] text-emerald-700 mt-0.5">✓ {roleById(z.correct)?.label}</span>}
              </button>
            );
          })}
        </div>
      </div>
    </BeatShell>
  );
}
